/**
 * IssueCreditMemoDialog — seller-facing free-form credit memo issuance.
 *
 * Distinct from `AdjustInvoiceModal` (which edits line quantities):
 * this dialog lets a seller refund an arbitrary $ amount on any paid /
 * shipped / delivered invoice with a one-word reason. Refund goes to
 * the buyer's verqo wallet. Money moves at confirm-time.
 *
 * Flow:
 *   1. Pick reason from /credit-memos/reasons (10 options)
 *   2. Enter amount + optional note (note required for `other`)
 *   3. PaymentApprovalModal → owner phone SMS approval → approval_token
 *   4. POST /invoices/{id}/credit-memos with approval_token
 *
 * Caller:
 *   <IssueCreditMemoDialog open={open} onClose={…} invoice={inv}
 *                          onSuccess={() => reload()}/>
 */
import { useEffect, useMemo, useState } from "react";
import { Receipt, Loader2, Wallet, AlertCircle, Info } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { toast } from "sonner";
import api, { formatErr, money } from "../lib/api";
import PaymentApprovalModal from "./PaymentApprovalModal";

export default function IssueCreditMemoDialog({ open, onClose, invoice, onSuccess }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason]   = useState("");
  const [amount, setAmount]   = useState("");
  const [note, setNote]       = useState("");
  const [busy, setBusy]       = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason(""); setAmount(""); setNote(""); setBusy(false); setApprovalOpen(false);
      return;
    }
    api.get("/credit-memos/reasons")
      .then(r => setReasons(r.data?.reasons || []))
      .catch(() => setReasons([]));
  }, [open]);

  const amountNum = Number(amount) || 0;
  const invoiceTotal = Number(invoice?.amount) || 0;
  const cmTotal = Number(invoice?.credit_memo_total) || 0;
  const remaining = Math.max(0, invoiceTotal - cmTotal);
  const overInvoice = amountNum > remaining;
  const noteRequired = reason === "other" && !note.trim();
  const canSubmit = !!reason && amountNum > 0 && !noteRequired && !busy;

  const submitWithApproval = async (approval_token) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/invoices/${invoice.id}/credit-memos`,
        { amount: amountNum, reason, note: note || undefined, approval_token },
      );
      toast.success(`Credit memo ${data.code} issued · ${money(data.amount)} to buyer wallet`);
      onSuccess?.(data);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(formatErr(detail) || "Could not issue credit memo");
    } finally { setBusy(false); }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setApprovalOpen(true);
  };

  if (!invoice) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
        <DialogContent className="max-w-md" data-testid="issue-credit-memo-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-4 h-4"/>
              Issue credit memo
            </DialogTitle>
            <div className="text-[12px] text-foreground/60 mt-1">
              {invoice.code} · {invoice.buyer_name} · {money(invoiceTotal)}
              {cmTotal > 0 && (
                <span className="ml-1.5 text-emerald-700">
                  · {money(cmTotal)} already credited
                </span>
              )}
            </div>
          </DialogHeader>

          <div className="rounded-lg bg-secondary/40 border border-border px-3 py-2.5 text-[12px] text-foreground/70 leading-relaxed flex items-start gap-2">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
            <span>
              Use this for goodwill, billing errors, freight true-ups, or any refund
              that doesn't require editing line quantities. The amount is credited to
              the buyer's verqo wallet immediately — money moves at confirm.
            </span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[12px] mb-1.5 block">Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="cm-reason-select" className="h-11">
                  <SelectValue placeholder="Pick a reason…"/>
                </SelectTrigger>
                <SelectContent>
                  {reasons.map(r => (
                    <SelectItem key={r.key} value={r.key} data-testid={`cm-reason-${r.key}`}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[12px] mb-1.5 block">Amount (USD)</Label>
              <Input
                type="number" min="0.01" step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="h-11 display text-xl"
                data-testid="cm-amount"
              />
              <div className="flex items-center gap-2 mt-1.5 text-[11px] text-foreground/55">
                <span>Remaining refundable: <span className="font-medium text-foreground/75">{money(remaining)}</span></span>
                <button type="button"
                  onClick={() => setAmount(String(remaining))}
                  className="ml-auto text-foreground/65 underline hover:text-foreground"
                  data-testid="cm-amount-max">
                  Use max
                </button>
              </div>
              {overInvoice && (
                <div className="mt-1.5 text-[11.5px] text-amber-700 inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3"/>
                  Amount exceeds invoice total — backend allows this, but double-check.
                </div>
              )}
            </div>

            <div>
              <Label className="text-[12px] mb-1.5 block">
                Note {reason === "other" && <span className="text-rose-600">*</span>}
                <span className="text-foreground/45 ml-1.5 text-[11px]">(optional)</span>
              </Label>
              <Textarea
                rows={3} maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={reason === "other" ? "Required when reason is 'Other' — what happened?" : "Optional context for the buyer + audit log"}
                data-testid="cm-note"
              />
              <div className="text-[11px] text-foreground/40 mt-1">{note.length}/500</div>
            </div>

            <div className="flex items-center justify-between pt-1 border-t border-border">
              <div className="text-[12px] text-foreground/65 inline-flex items-center gap-1.5">
                <Wallet className="w-3.5 h-3.5"/>
                Credits buyer wallet
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="rounded-full" onClick={onClose} data-testid="cm-cancel">
                  Cancel
                </Button>
                <Button type="submit" disabled={!canSubmit} data-testid="cm-submit"
                        className="rounded-full bg-foreground text-background hover:bg-foreground/90">
                  {busy
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/>Issuing…</>
                    : <>Issue {amountNum > 0 ? money(amountNum) : ""}</>}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <PaymentApprovalModal
        open={approvalOpen}
        onClose={() => setApprovalOpen(false)}
        action="issue_credit_memo"
        invoiceId={invoice.id}
        amount={amountNum}
        title={`Approve ${money(amountNum)} credit memo`}
        description={`Refunds ${money(amountNum)} from your wallet to ${invoice.buyer_name}.`}
        onApproved={submitWithApproval}
      />
    </>
  );
}
